/**
 * PII / secret masking utilities.
 * Used by Sentry beforeSend, logger, and audit trail serialization.
 *
 * Rule: NEVER let secrets into logs, Sentry, or the customer-facing UI.
 * Every field listed in MASKED_KEYS is replaced with a sentinel string.
 */

export const MASKED_VALUE = '[REDACTED]';

/**
 * Case-insensitive key matchers.
 * These match exact key names OR keys containing these substrings.
 */
export const MASKED_KEY_PATTERNS = [
  // Auth
  /authorization/i,
  /^cookie$/i,
  /^set-cookie$/i,
  /password/i,
  /passwd/i,
  /secret/i,
  /^token$/i,
  /access[_-]?token/i,
  /refresh[_-]?token/i,
  /id[_-]?token/i,
  /^api[_-]?key$/i,
  /^apikey$/i,
  /bearer/i,
  /jwt/i,

  // Session
  /session[_-]?id/i,
  /session[_-]?token/i,
  /csrf[_-]?token/i,

  // Financial
  /credit[_-]?card/i,
  /card[_-]?number/i,
  /^cvv$/i,
  /^cvc$/i,
  /stripe[_-]?secret/i,

  // PII
  /ssn/i, // US social security
  /mynumber/i, // Japan mynumber
  /passport/i,
  /driver[_-]?license/i,
  // PR-AUDIT-PII (L2 M-11 + M-13): audit_logs metadata 経由の email / メール本文
  // が CSV export / Sentry / log で漏出するのを防ぐ。完全一致の "email" /
  // "user_email" / "target_email" / "to_email" 等を対象にする。
  /^email$/i,
  /^user[_-]?email$/i,
  /^target[_-]?email$/i,
  /^to[_-]?email$/i,
  /^recipient[_-]?email$/i,
  // メール件名 / 本文 (admin から user に手動送信した内容を audit に残すが、
  // CSV export 時に本文をそのまま外出ししない)。
  /^subject$/i,
  /^email[_-]?subject$/i,
  /^body$/i,
  /^email[_-]?body$/i,

  // Environment
  /private[_-]?key/i,
  /^db[_-]?url$/i,
  /database[_-]?url/i,
  /webhook[_-]?secret/i,
] as const;

/**
 * Value patterns that look like secrets regardless of key name.
 * Matched against string values; if detected, the value is replaced.
 */
export const MASKED_VALUE_PATTERNS = [
  // PetaCMS API keys
  /\bpetacms_(live|test)_[A-Za-z0-9]{32,}/g,
  // Stripe keys
  /\bsk_(live|test)_[A-Za-z0-9]{24,}/g,
  /\brk_(live|test)_[A-Za-z0-9]{24,}/g,
  /\bwhsec_[A-Za-z0-9]{24,}/g,
  // JWT-ish strings (header.payload.signature, base64url)
  /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g,
  // AWS-style secrets
  /\bAKIA[0-9A-Z]{16}\b/g,
  // Generic bearer tokens (40+ chars of base64url after "bearer ")
  /bearer\s+[A-Za-z0-9_\-.=]{20,}/gi,
] as const;

const shouldMaskKey = (key: string): boolean => MASKED_KEY_PATTERNS.some((re) => re.test(key));

const maskStringValue = (value: string): string => {
  let masked = value;
  for (const pattern of MASKED_VALUE_PATTERNS) {
    masked = masked.replace(pattern, MASKED_VALUE);
  }
  return masked;
};

/**
 * Deeply mask an object in-place-safely (returns new object).
 * Handles Maps, Arrays, plain objects, and primitives.
 * Does NOT handle class instances or circular references;
 * pass plain JSON-ish data.
 */
export const maskObject = <T>(input: T, seen = new WeakSet<object>()): T => {
  if (input === null || input === undefined) return input;
  if (typeof input === 'string') {
    return maskStringValue(input) as unknown as T;
  }
  if (typeof input !== 'object') return input;

  // Prevent infinite loops on cycles
  if (seen.has(input as object)) return input;
  seen.add(input as object);

  if (Array.isArray(input)) {
    return input.map((item) => maskObject(item, seen)) as unknown as T;
  }

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (shouldMaskKey(key)) {
      result[key] = MASKED_VALUE;
      continue;
    }
    result[key] = maskObject(value, seen);
  }
  return result as unknown as T;
};

/**
 * Mask headers specifically - header keys are case-insensitive.
 * Works on Headers, plain objects, and entries arrays.
 */
export const maskHeaders = (
  headers: Headers | Record<string, string> | Array<[string, string]>,
): Record<string, string> => {
  const entries: Array<[string, string]> =
    headers instanceof Headers
      ? [...headers.entries()]
      : Array.isArray(headers)
        ? headers
        : Object.entries(headers);

  const result: Record<string, string> = {};
  for (const [key, value] of entries) {
    result[key] = shouldMaskKey(key) ? MASKED_VALUE : maskStringValue(value);
  }
  return result;
};

/**
 * Mask URL query string - keeps path, scrubs params named like secrets.
 */
export const maskUrl = (url: string): string => {
  try {
    const u = new URL(url);
    for (const key of [...u.searchParams.keys()]) {
      if (shouldMaskKey(key)) {
        u.searchParams.set(key, MASKED_VALUE);
      }
    }
    return u.toString();
  } catch {
    return url;
  }
};
